import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import {
  addTenantSubscription,
  renewCarryForward,
  computeRenewalPeriods,
  fetchLookups,
  fetchTenantSubscriptions,
  billingCycleToRegisterSubscription,
  updateSubscriptionSettings,
  isSubscriptionStatusOperable,
} from '../../services/licensingApi';

/** 7 fields for Add / Edit subscription term. */
const emptyForm = {
  subscriptionStatus: 'ACTIVE',
  billingCycle: 'MONTHLY',
  currency: 'USD',
  gracePeriodDays: '30',
  overagePolicy: 'BLOCK',
  startDate: '',
  endDate: '',
};

const dash = (v) => (v == null || v === '' ? '—' : v);

const TenantSubscriptionsDrawer = ({ open, onClose, tenantId, tenantName }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [lookups, setLookups] = useState({});
  const [renewing, setRenewing] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [carryForward, setCarryForward] = useState(true);
  const [renewSourceId, setRenewSourceId] = useState(null);
  const [renewPeriods, setRenewPeriods] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const statusOptions = lookups.SUBSCRIPTION_STATUS || [];
  const billingOptions = lookups.BILLING_CYCLE || [];
  const currencyOptions = lookups.CURRENCY || [];
  const overageOptions = lookups.OVERAGE_POLICY || [
    { code: 'BLOCK', label: 'Block at 100%' },
    { code: 'ALLOW', label: 'Allow billable overage' },
  ];

  const selectedRow = useMemo(
    () => rows.find((r) => String(r.id) === String(selectionModel[0])) || null,
    [rows, selectionModel]
  );

  const ensureLookups = async () => {
    if (Object.keys(lookups).length) return;
    try {
      const lu = await fetchLookups({ grouped: true });
      setLookups(lu && typeof lu === 'object' ? lu : {});
    } catch (e) {
      console.error(e);
    }
  };

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await fetchTenantSubscriptions(tenantId);
      setRows(
        (list || []).map((s) => ({
          id: s.subscriptionId,
          subscriptionId: s.subscriptionId,
          licenseNumber: s.licenseNumber,
          status: s.subscriptionStatus || '',
          billingCycle: s.billingCycle || '',
          currency: s.currency || '',
          gracePeriodDays: s.gracePeriodDays != null ? s.gracePeriodDays : '',
          overagePolicy: s.overagePolicy || '',
          startDate: s.startDate || '',
          endDate: s.endDate || '',
          autoRenew: !!s.autoRenew,
        }))
      );
      setSelectionModel([]);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load subscriptions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) load();
  }, [open, tenantId, load]);

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const openRowMenu = (event, row) => {
    event.stopPropagation();
    setSelectionModel([row.id]);
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const openRenewDialog = (row) => {
    if (!tenantId || rows.length === 0) return;
    const source = row || menuRow || selectedRow;
    closeRowMenu();
    setRenewSourceId(source?.subscriptionId || null);
    setRenewPeriods(
      computeRenewalPeriods({
        startDate: source?.startDate,
        endDate: source?.endDate,
        billingCycle: source?.billingCycle,
      })
    );
    setCarryForward(true);
    setRenewDialogOpen(true);
  };

  const confirmRenew = async () => {
    if (!tenantId) return;
    if (!carryForward) {
      setRenewDialogOpen(false);
      await openAddForm();
      toast.info('Enter the new term settings, then save. License details are not copied.');
      return;
    }
    const sourceId = renewSourceId || selectedRow?.subscriptionId || null;
    setRenewing(true);
    try {
      const created = await renewCarryForward(tenantId, {
        sourceSubscriptionId: sourceId || undefined,
        createdBy: 'cdx',
      });
      toast.success(
        `Renewed · License # ${created.licenseNumber || created.subscriptionId}. Invoice created — send from Invoices when ready.`
      );
      setRenewDialogOpen(false);
      setRenewSourceId(null);
      setSelectionModel([]);
      await load();
      if (created.subscriptionId) {
        openLicense(created.subscriptionId);
      }
    } catch (e) {
      toast.error(e.message || 'Renew failed');
    } finally {
      setRenewing(false);
    }
  };

  const openAddForm = async () => {
    setFormMode('add');
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
    await ensureLookups();
  };

  const openEditSelected = async (rowOverride) => {
    const row = rowOverride || menuRow || selectedRow;
    closeRowMenu();
    if (!row?.subscriptionId) {
      toast.info('Select a subscription row first');
      return;
    }
    if (!isSubscriptionStatusOperable(row.status)) {
      toast.warning(
        `Subscription is ${String(row.status).toUpperCase()}. It cannot be edited — use Renew or Add a new term.`
      );
      return;
    }
    setFormMode('edit');
    setEditingId(row.subscriptionId);
    setForm({
      subscriptionStatus: row.status || 'ACTIVE',
      billingCycle: row.billingCycle || 'MONTHLY',
      currency: row.currency || 'USD',
      gracePeriodDays:
        row.gracePeriodDays !== '' && row.gracePeriodDays != null
          ? String(row.gracePeriodDays)
          : '',
      overagePolicy: row.overagePolicy || 'BLOCK',
      startDate: row.startDate || '',
      endDate: row.endDate || '',
    });
    setFormOpen(true);
    await ensureLookups();
  };

  const openLicense = useCallback(
    (subscriptionId) => {
      if (!tenantId || !subscriptionId) return;
      onClose?.();
      navigate(`/admin/license?tenantId=${tenantId}&subscriptionId=${subscriptionId}`);
    },
    [navigate, onClose, tenantId]
  );

  const handleDeleteSelected = async () => {
    const row = menuRow || selectedRow;
    if (!row?.subscriptionId) return;
    if (!isSubscriptionStatusOperable(row.status)) {
      toast.warning(
        `Subscription is already ${String(row.status).toUpperCase()}. No further cancel/edit.`
      );
      setDeleteConfirmOpen(false);
      closeRowMenu();
      return;
    }
    setDeleting(true);
    try {
      await updateSubscriptionSettings(row.subscriptionId, {
        subscriptionStatus: 'CANCELLED',
      });
      toast.success('Subscription cancelled');
      setDeleteConfirmOpen(false);
      closeRowMenu();
      setSelectionModel([]);
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to cancel subscription');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      if (formMode === 'edit' && editingId) {
        const payload = {
          subscriptionStatus: form.subscriptionStatus,
          billingCycle: form.billingCycle,
          currency: form.currency,
          overagePolicy: form.overagePolicy || 'BLOCK',
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
        };
        if (form.gracePeriodDays !== '' && form.gracePeriodDays != null) {
          payload.gracePeriodDays = Number(form.gracePeriodDays);
        }
        await updateSubscriptionSettings(editingId, payload);
        toast.success('Subscription updated');
        setFormOpen(false);
        setEditingId(null);
        setForm(emptyForm);
        await load();
        return;
      }

      const body = {
        planName: 'Trial',
        billingCycle: form.billingCycle,
        subscription: billingCycleToRegisterSubscription(form.billingCycle),
        subscriptionStatus: form.subscriptionStatus,
        currency: form.currency,
        overagePolicy: form.overagePolicy,
        autoRenew: true,
        numberOfUsers: 5,
        expirePrevious: true,
      };
      if (form.gracePeriodDays !== '' && form.gracePeriodDays != null) {
        body.gracePeriodDays = Number(form.gracePeriodDays);
      }
      if (form.startDate) body.startDate = form.startDate;
      if (form.endDate) body.endDate = form.endDate;

      const created = await addTenantSubscription(tenantId, body);
      toast.success(`Subscription added · License # ${created.licenseNumber || created.subscriptionId}`);
      setFormOpen(false);
      setForm(emptyForm);
      await load();
      if (created.subscriptionId) {
        openLicense(created.subscriptionId);
      }
    } catch (e) {
      toast.error(e.message || (formMode === 'edit' ? 'Failed to update subscription' : 'Failed to add subscription'));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        valueFormatter: (v) => dash(v),
      },
      {
        field: 'billingCycle',
        headerName: 'Billing cycle',
        flex: 1,
        minWidth: 110,
        valueFormatter: (v) => dash(v),
      },
      {
        field: 'currency',
        headerName: 'Currency',
        flex: 0.7,
        minWidth: 90,
        valueFormatter: (v) => dash(v),
      },
      {
        field: 'gracePeriodDays',
        headerName: 'Grace period (days)',
        flex: 0.8,
        minWidth: 120,
        valueFormatter: (v) => dash(v),
      },
      {
        field: 'overagePolicy',
        headerName: 'Overage at 100%',
        flex: 1,
        minWidth: 120,
        valueFormatter: (v) => dash(v),
      },
      {
        field: 'startDate',
        headerName: 'Start date',
        flex: 1,
        minWidth: 110,
        valueFormatter: (v) => dash(v),
      },
      {
        field: 'endDate',
        headerName: 'End / renewal',
        flex: 1,
        minWidth: 110,
        valueFormatter: (v) => dash(v),
      },
      {
        field: 'actions',
        headerName: '',
        width: 52,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <IconButton
            size="small"
            aria-label="Row actions"
            onClick={(e) => openRowMenu(e, params.row)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    []
  );

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const selectionLocked =
    selectedRow != null && !isSubscriptionStatusOperable(selectedRow.status);
  const menuRowOperable = menuRow != null && isSubscriptionStatusOperable(menuRow.status);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 720, md: 900 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <HistoryEduIcon color="primary" sx={{ mr: 0.75, fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3} noWrap>
              Subscriptions
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {tenantName ? `${tenantName} (#${tenantId})` : `Tenant #${tenantId}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
          <Alert
            severity="info"
            sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem', py: 0.25 } }}
          >
            Use the row <strong>⋮</strong> menu for Renew, Edit, Cancel, or open License.
            Renew asks whether to carry forward settings. Email from Invoices →{' '}
            <strong>Send email…</strong> after you check the invoice.
          </Alert>

          {selectionLocked && (
            <Alert
              severity="warning"
              sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem', py: 0.25 } }}
            >
              Selected subscription is {String(selectedRow.status).toUpperCase()} — Edit and Cancel are
              locked. Use ⋮ → Renew to carry forward, or Add for a custom new term.
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={load} disabled={loading} aria-label="Refresh">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Add subscription (custom settings)">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={openAddForm}
                  disabled={!tenantId}
                  aria-label="Add"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 280 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                density="compact"
                pageSizeOptions={[5, 10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(model) => {
                  // Keep a single selection (toolbar Edit / Delete).
                  const ids = Array.isArray(model) ? model : [...(model?.ids || [])];
                  setSelectionModel(ids.slice(-1));
                }}
                onRowClick={(params) => setSelectionModel([params.id])}
                onRowDoubleClick={(params) => openLicense(params.row.subscriptionId)}
                slots={{
                  noRowsOverlay: () => (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography variant="body2" color="text.secondary">
                        No subscriptions yet
                      </Typography>
                    </Box>
                  ),
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '& .MuiDataGrid-columnHeaderTitle': {
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                }}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => openRenewDialog(menuRow)}
          disabled={!tenantId || renewing || !menuRow}
        >
          <ListItemIcon>
            <AutorenewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Renew…" />
        </MenuItem>
        <MenuItem
          onClick={() => openEditSelected(menuRow)}
          disabled={!menuRowOperable}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeRowMenu();
            if (menuRow?.subscriptionId) openLicense(menuRow.subscriptionId);
          }}
          disabled={!menuRow?.subscriptionId}
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Open License" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteConfirmOpen(true);
            setMenuAnchor(null);
          }}
          disabled={!menuRowOperable}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Cancel subscription" />
        </MenuItem>
      </Menu>

      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{formMode === 'edit' ? 'Edit subscription' : 'Add subscription'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.subscriptionStatus}
                  onChange={(e) => setField('subscriptionStatus', e.target.value)}
                >
                  {(statusOptions.length
                    ? statusOptions
                    : [
                        { code: 'ACTIVE', label: 'Active' },
                        { code: 'TRIAL', label: 'Trial' },
                        { code: 'GRACE_PERIOD', label: 'Grace Period' },
                        { code: 'SUSPENDED', label: 'Suspended' },
                        { code: 'EXPIRED', label: 'Expired' },
                        { code: 'CANCELLED', label: 'Cancelled' },
                      ]
                  ).map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.label || s.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Billing cycle</InputLabel>
                <Select
                  label="Billing cycle"
                  value={form.billingCycle}
                  onChange={(e) => setField('billingCycle', e.target.value)}
                >
                  {(billingOptions.length
                    ? billingOptions
                    : [
                        { code: 'MONTHLY', label: 'Monthly' },
                        { code: 'ANNUAL', label: 'Annual' },
                      ]
                  ).map((b) => (
                    <MenuItem key={b.code} value={b.code}>
                      {b.label || b.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value)}
                >
                  {(currencyOptions.length
                    ? currencyOptions
                    : [
                        { code: 'USD', label: 'USD' },
                        { code: 'EUR', label: 'EUR' },
                        { code: 'GBP', label: 'GBP' },
                        { code: 'INR', label: 'INR' },
                      ]
                  ).map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.code}
                      {c.label && c.label !== c.code ? ` — ${c.label}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Grace period (days)"
                value={form.gracePeriodDays}
                onChange={(e) => setField('gracePeriodDays', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Overage at 100%</InputLabel>
                <Select
                  label="Overage at 100%"
                  value={form.overagePolicy}
                  onChange={(e) => setField('overagePolicy', e.target.value)}
                >
                  {overageOptions.map((o) => (
                    <MenuItem key={o.code} value={o.code}>
                      {o.label || o.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start date"
                InputLabelProps={{ shrink: true }}
                value={form.startDate}
                onChange={(e) => setField('startDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End / renewal"
                InputLabelProps={{ shrink: true }}
                value={form.endDate}
                onChange={(e) => setField('endDate', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renewDialogOpen}
        onClose={() => !renewing && setRenewDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Renew subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Creates a new term and invoice. If you renew before the current end date, the new
            term starts the day after — no overlap. No email until Invoices → Send email….
          </Typography>
          {renewPeriods && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Current active period
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {renewPeriods.currentStart || '—'} → {renewPeriods.currentEnd || '—'}
                {renewPeriods.stillActive ? ' (still active)' : ' (ended / expired)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Next term (no overlap) · {renewPeriods.billingCycle || 'MONTHLY'}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {renewPeriods.nextStart || '—'} → {renewPeriods.nextEnd || '—'}
              </Typography>
              {renewPeriods.stillActive && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Current term stays in force until {renewPeriods.currentEnd}. The new term becomes
                  current after that date.
                </Typography>
              )}
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={carryForward}
                onChange={(e) => setCarryForward(e.target.checked)}
                disabled={renewing}
              />
            }
            label="Carry forward all existing license settings"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            {carryForward
              ? 'Products, entitlements, connectors, flows, and LLMs stay as-is. Dates follow the next term above.'
              : 'Opens Add so you can enter a custom term. License details are not copied.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setRenewDialogOpen(false)} disabled={renewing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmRenew}
            disabled={renewing}
            startIcon={renewing ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon />}
          >
            {renewing ? 'Renewing…' : carryForward ? 'Renew' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel subscription?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This sets status to <strong>CANCELLED</strong>. The row stays in history (same as EXPIRED).
            It is not hard-deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Keep
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteSelected}
            disabled={deleting}
          >
            {deleting ? 'Cancelling…' : 'Cancel subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TenantSubscriptionsDrawer;
